import { useCallback, useRef, useState } from 'react';
import { TRAVELING_SALESMAN_CHEAPER_HAND_DURATION_MS } from '../config/travelingSalesmanCheaperHandEffect';

/**
 * Traveling Salesman (192): cheaper.png on all hand cards for 2s when discount applies.
 */
export function useTravelingSalesmanCheaperHand() {
  const consumedAnimationKeysRef = useRef(new Set());
  const timeoutIdRef = useRef(null);
  const [cheaperHandOverlayUntil, setCheaperHandOverlayUntil] = useState(0);
  const [cheaperHandTick, setCheaperHandTick] = useState(0);

  const queuePulses = useCallback((grants) => {
    if (!Array.isArray(grants) || grants.length === 0) {
      return;
    }

    let queued = false;
    grants.forEach((grant) => {
      if (!grant?.key) {
        throw new Error('useTravelingSalesmanCheaperHand.queuePulses: grant key is required');
      }
      if (consumedAnimationKeysRef.current.has(grant.key)) {
        return;
      }
      consumedAnimationKeysRef.current.add(grant.key);
      queued = true;
    });

    if (!queued) {
      return;
    }

    const durationMs = Number(grants[0]?.durationMs) > 0
      ? Number(grants[0].durationMs)
      : TRAVELING_SALESMAN_CHEAPER_HAND_DURATION_MS;

    if (timeoutIdRef.current != null) {
      window.clearTimeout(timeoutIdRef.current);
    }

    const until = Date.now() + durationMs;
    setCheaperHandOverlayUntil(until);
    setCheaperHandTick((tick) => tick + 1);

    timeoutIdRef.current = window.setTimeout(() => {
      timeoutIdRef.current = null;
      setCheaperHandOverlayUntil(0);
      setCheaperHandTick((tick) => tick + 1);
    }, durationMs);
  }, []);

  const isCheaperHandOverlayActive = useCallback(() => {
    void cheaperHandTick;
    return cheaperHandOverlayUntil > Date.now();
  }, [cheaperHandOverlayUntil, cheaperHandTick]);

  const reset = useCallback(() => {
    if (timeoutIdRef.current != null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    consumedAnimationKeysRef.current.clear();
    setCheaperHandOverlayUntil(0);
    setCheaperHandTick((tick) => tick + 1);
  }, []);

  return {
    queuePulses,
    isCheaperHandOverlayActive,
    reset,
  };
}

export default useTravelingSalesmanCheaperHand;
